"use client"

import {
    type FieldError,
    type FieldErrorCode,
    type FieldValidator
} from "@/app/(site)/_components/Atoms/Inputs/errors/fieldErrorCodes"
import { useFieldValidation } from "@/app/(site)/_components/Atoms/Inputs/errors/useFieldValidation"
import {
    fieldValueTextClasses,
    inactiveTextClasses
} from "@/app/(site)/_components/Atoms/Inputs/input-text-classes"
import InputErrors from "@/app/(site)/_components/Atoms/Inputs/InputErrors"
import { Skeleton } from "@/app/(site)/_components/Atoms/Skeleton"
import { type FontStylesWithoutDot } from "@/config/fontConfig/fontStyles"
import { FOCUS_VISIBLE_BORDER_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import React, { useCallback, useEffect, useState, type InputHTMLAttributes } from "react"

export const INPUT_DEFAULT_HEIGHT = 48

/**
 * Same type as the `input` element's `onBlur`, but it accepts an additional parameter
 * which is the validation state.
 */
type InputOnBlur = (
    ...args: [
        ...Parameters<
            Exclude<InputHTMLAttributes<HTMLInputElement>["onBlur"], undefined>
        >,
        boolean
    ]
) => ReturnType<Exclude<InputHTMLAttributes<HTMLInputElement>["onBlur"], undefined>>

export interface InputProps extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "id" | "height" | "width" | "prefix" | "pattern" | "onBlur"
> {
    autoComplete?: string
    height?: string
    label: string
    name: string
    displayMaxLengthIndicator?: boolean
    typography?: FontStylesWithoutDot
    inputPadding?: string
    prefix?: React.ReactNode
    suffix?: React.ReactNode
    customValidators?: FieldValidator[]
    /** Decided elsewhere — a PFAS restriction, a Loqate verdict — rather than by a check here. */
    externalErrors?: Array<FieldErrorCode | FieldError>
    customInputAttributes?: Record<string, string | undefined>
    showInlineErrors?: boolean
    width?: string
    containerClassName?: string
    labelClassName?: string
    inputClassName?: string
    errorClassName?: string
    value?: string
    isLoading?: boolean
    labelTypography?: FontStylesWithoutDot
    onBlurValidation?: boolean
    onBlur?: InputOnBlur
    langCode: string
    pattern?: RegExp
    tabIndex?: number
    /** Which code a failed `pattern` or `type` means here, e.g. an email over a phone number. */
    invalidValueCode?: FieldErrorCode
    messageParams?: Record<string, string | number>
    countryCode?: string
    validateCharacters?: boolean
    allowedCharacters?: string
    isEmailInput?: boolean
}

export default function Input({
    isEmailInput = false,
    autoComplete = "",
    customInputAttributes = {},
    customValidators,
    externalErrors,
    defaultValue,
    disabled = false,
    height = `${INPUT_DEFAULT_HEIGHT}px`,
    inputPadding,
    label,
    isLoading = false,
    maxLength = 100,
    name,
    onBlur,
    onChange,
    pattern,
    placeholder = "",
    prefix,
    required = false,
    suffix,
    type = "text",
    typography = "Trade_Gothic_Text_16_150_0_300",
    labelTypography = "Trade_Gothic_Text_12_150_0_300",
    showInlineErrors = false,
    width = "100%",
    containerClassName = "",
    labelClassName = "",
    inputClassName = "",
    value,
    onBlurValidation = false,
    langCode,
    tabIndex,
    invalidValueCode,
    messageParams,
    countryCode,
    validateCharacters = true,
    allowedCharacters,
    ...props
}: InputProps) {
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const [hasValue, setHasValue] = useState<boolean>(!!(value || defaultValue))

    const { elementRef, errors, hasErrors, errorCode, validate, clear } =
        useFieldValidation({
            name,
            langCode,
            validators: customValidators,
            allowedCharacters,
            countryCode,
            externalErrors,
            validateCharacters,
            invalidValueCode,
            messageParams
        })

    const errorId = `${name}-errors`
    const showFloatingLabel = isFocused || hasValue || !!placeholder

    useEffect(() => {
        /**
         * Only ever to release. A marked field is re-checked so a correction clears itself,
         * including one the form wrote — accepting Loqate's suggestion fires no change event.
         * Judging an unmarked field here would accuse the customer mid-word, since half a
         * street name can read as a PO box long before they have finished typing it.
         */
        if (hasErrors && value && value.length > 0 && !isEmailInput) {
            validate()
        }
        setHasValue(!!(value || defaultValue))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, defaultValue])

    const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setHasValue(!!ev.target.value)
        onChange?.(ev)

        if (hasErrors) {
            clear()
        }
    }

    const handleFocus = () => setIsFocused(true)

    const handleBlur = useCallback(
        (ev: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(false)
            setHasValue(!!ev.target.value)
            const isValid = onBlurValidation ? validate() : !hasErrors
            onBlur?.(ev, isValid)
        },
        [hasErrors, onBlur, onBlurValidation, validate]
    )

    const intputElementPadding = inputPadding
        ? ""
        : prefix && suffix
          ? "px-12 py-3"
          : prefix
            ? "py-3 pl-10 pr-4"
            : suffix
              ? "py-3 pl-4 pr-10"
              : "px-12 pt-24"

    return (
        <div
            className={clsx(containerClassName, "inline-block w-full")}
            data-error={errorCode}
        >
            <div className="relative">
                <label
                    htmlFor={name}
                    className={clsx(
                        "relative block",
                        disabled && "cursor-not-allowed",
                        labelClassName
                    )}
                    aria-label={label}
                    aria-required={required}
                >
                    <div className="relative w-full">
                        {prefix && (
                            <div className="absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-500">
                                {prefix}
                            </div>
                        )}

                        <input
                            tabIndex={tabIndex}
                            ref={elementRef}
                            id={name}
                            name={name}
                            type={type}
                            autoComplete={autoComplete}
                            value={value}
                            defaultValue={defaultValue}
                            disabled={disabled || isLoading}
                            maxLength={maxLength > 0 ? maxLength : undefined}
                            pattern={pattern?.source}
                            placeholder={isLoading ? "" : placeholder}
                            className={clsx(
                                "w-full rounded-lg border bg-white shadow-sm transition-colors duration-200",
                                FOCUS_VISIBLE_BORDER_CLASS,
                                fieldValueTextClasses(hasValue),
                                typography,
                                intputElementPadding,
                                `h-[${INPUT_DEFAULT_HEIGHT}px] min-h-[${INPUT_DEFAULT_HEIGHT}px]`,
                                hasErrors
                                    ? "border-col-border-alert"
                                    : disabled
                                      ? "cursor-not-allowed opacity-30"
                                      : isFocused
                                        ? "border-col-border-strong"
                                        : "border-border-neutral hover:border-black",
                                inputClassName
                            )}
                            style={
                                inputPadding
                                    ? { padding: inputPadding, height }
                                    : { height }
                            }
                            aria-invalid={hasErrors}
                            aria-describedby={hasErrors ? errorId : undefined}
                            aria-required={required}
                            required={required}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onChange={handleInputChange}
                            onInvalid={e => {
                                e.preventDefault() // Prevent browser's default validation tooltip
                                validate()
                            }}
                            {...customInputAttributes}
                            {...props}
                        />

                        {isLoading && (
                            <Skeleton className="absolute left-4 right-4 top-1/2 z-10 h-[35px] -translate-y-1/2" />
                        )}

                        {suffix && (
                            <div className="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-gray-500">
                                {suffix}
                            </div>
                        )}

                        {label && (
                            <span
                                className={clsx(
                                    "pointer-events-none absolute left-13 select-none transition-all duration-200 ease-in-out",
                                    inactiveTextClasses,
                                    typography,
                                    isLoading && "opacity-0",
                                    showFloatingLabel
                                        ? "top-2 origin-left scale-75"
                                        : "top-1/2 -translate-y-1/2 scale-100",
                                    disabled && "opacity-30",
                                    prefix && !showFloatingLabel ? "left-10" : "",
                                    isFocused || hasValue ? "pt-0" : "pt-4"
                                )}
                            >
                                {label}
                                {required && "*"}
                            </span>
                        )}
                    </div>
                </label>
                {!showInlineErrors && hasErrors && (
                    <InputErrors errors={errors} id={errorId} className="pl-12 pt-6" />
                )}
            </div>
        </div>
    )
}
